import { useRef, useState, useEffect } from 'react';

/**
 * SignaturePad — canvas-based signature capture, mouse + touch.
 * Returns a data URL via onCapture(dataUrl) when parent presses "Accept".
 * v2.5.885
 */
export default function SignaturePad({ height = 160, onCapture, signerName, setSignerName, disabled = false }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const lastRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    // set CSS size vs pixel size for DPR
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, c.width, c.height);
  }, []);

  const pos = (ev) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = ev.touches?.[0];
    const clientX = t ? t.clientX : ev.clientX;
    const clientY = t ? t.clientY : ev.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (ev) => {
    if (disabled) return;
    ev.preventDefault();
    setDrawing(true);
    lastRef.current = pos(ev);
  };
  const move = (ev) => {
    if (!drawing || disabled) return;
    ev.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(ev);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    setHasInk(true);
  };
  const end = () => setDrawing(false);

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, c.width, c.height);
    setHasInk(false);
  };

  const accept = () => {
    if (!hasInk) return;
    const c = canvasRef.current;
    const dataUrl = c.toDataURL('image/png');
    onCapture(dataUrl);
  };

  return (
    <div className="space-y-2" data-testid="signature-pad">
      <label className="text-xs text-gray-medium">Signer name</label>
      <input
        type="text"
        value={signerName || ''}
        onChange={(e) => setSignerName(e.target.value)}
        placeholder="Full name"
        disabled={disabled}
        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        data-testid="signature-signer-name"
      />
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height, touchAction: 'none' }}
        className="border-2 border-dashed border-slate-300 rounded-lg bg-white cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid="signature-canvas"
      />
      <div className="flex gap-2">
        <button type="button" onClick={clear} disabled={disabled || !hasInk} className="text-xs px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40" data-testid="signature-clear">Clear</button>
        <button type="button" onClick={accept} disabled={disabled || !hasInk || !signerName?.trim()} className="text-xs px-3 py-1.5 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-40" data-testid="signature-accept">Accept &amp; Sign</button>
      </div>
    </div>
  );
}
